/*
 * carousel — Level 2 (CSP build) progressive enhancement over a native CSS
 * scroll-snap track.
 *
 * Without JS the track is already a horizontally scrollable, snapping row of
 * slides (see resources/css/components/_carousel.css). This component only adds
 * prev/next buttons and a slide counter, and it derives ALL of its state —
 * current slide, edge-disabled — PURELY from the track's scroll position. There
 * is no JS-authored slide model to drift out of sync with the DOM.
 *
 * Compositor discipline: layout is read (offsetLeft / clientWidth / scrollWidth)
 * ONCE in measure() on init + resize and cached. During scroll only the cheap
 * scrollLeft is read, and that read is rAF-throttled so a burst of scroll events
 * can't thrash layout. Nothing here reads layout inside the scroll handler.
 *
 * Registered in resources/js/app.js as: Alpine.data('carousel', carousel)
 * Paired with resources/views/components/interactive/carousel.antlers.html.
 */
export default function carousel() {
    return {
        atStart: true,
        atEnd: false,
        index: 0,
        total: 0,

        // Cached layout (set in measure(); never read during scroll).
        _slideStep: 0,
        _maxScroll: 0,
        _rafId: null,
        _reduceMotion: false,
        _onResize: null,

        init() {
            const track = this.$refs.track;
            if (!track) {
                return;
            }

            this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            this.measure();
            this.updateFromScroll();

            // Slide widths change on resize — re-measure then (never during scroll).
            this._onResize = () => {
                this.measure();
                this.updateFromScroll();
            };
            window.addEventListener('resize', this._onResize, { passive: true });
        },

        /**
         * Cache the slide step (distance between consecutive slide starts) and the
         * maximum scroll offset. Reads layout — only ever called on init + resize.
         */
        measure() {
            const track = this.$refs.track;
            if (!track) {
                return;
            }

            const slides = Array.from(track.querySelectorAll('[data-carousel-slide]'));
            this.total = slides.length;

            if (slides.length >= 2) {
                this._slideStep = slides[1].offsetLeft - slides[0].offsetLeft;
            } else if (slides.length === 1) {
                this._slideStep = slides[0].offsetWidth;
            } else {
                this._slideStep = track.clientWidth;
            }

            this._maxScroll = track.scrollWidth - track.clientWidth;
        },

        /**
         * Scroll handler bound via @scroll.passive. rAF-throttled: at most one
         * scrollLeft read per frame, no other layout reads.
         */
        onScroll() {
            if (this._rafId !== null) {
                return;
            }
            this._rafId = requestAnimationFrame(() => {
                this._rafId = null;
                this.updateFromScroll();
            });
        },

        /**
         * Derive current index + edge-disabled state from scrollLeft alone, using
         * the cached dimensions. A 1px tolerance absorbs sub-pixel rounding.
         */
        updateFromScroll() {
            const track = this.$refs.track;
            if (!track) {
                return;
            }

            const x = track.scrollLeft;
            // Index first, then derive atStart from it: rounding to the nearest slide
            // absorbs any leading offset (e.g. track padding) that would otherwise keep
            // scrollLeft a few px above 0 at rest and mis-read the first slide as scrolled.
            this.index = this._slideStep > 0 ? Math.round(x / this._slideStep) : 0;
            this.atStart = this.index <= 0;
            this.atEnd = x >= this._maxScroll - 1;
        },

        next() {
            this.scrollByStep(1);
        },

        prev() {
            this.scrollByStep(-1);
        },

        scrollByStep(direction) {
            const track = this.$refs.track;
            if (!track || this._slideStep <= 0) {
                return;
            }
            track.scrollBy({
                left: direction * this._slideStep,
                behavior: this._reduceMotion ? 'auto' : 'smooth',
            });
        },

        get counterLabel() {
            if (this.total === 0) {
                return '';
            }
            return `${Math.min(this.index + 1, this.total)} / ${this.total}`;
        },

        destroy() {
            if (this._rafId !== null) {
                cancelAnimationFrame(this._rafId);
                this._rafId = null;
            }
            if (this._onResize) {
                window.removeEventListener('resize', this._onResize);
                this._onResize = null;
            }
        },
    };
}
